import { useEffect, useMemo, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Stepper } from '../../../../components/Stepper';
import { Tooltip } from '../../../../components/Tooltip';
import { DateField } from '../../../../components/inputs/DateField';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { RadioGroup, type RadioOption } from '../../../../components/inputs/RadioGroup';
import { SearchTableInput } from '../../../../components/inputs/SearchTableInput';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { Textarea } from '../../../../components/inputs/Textarea';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { CorrespondentNameFields } from '../../../document-management/routing/components/CorrespondentNameFields';
import { EMPTY_NAME, anyNamePart, nameIncomplete } from '../../../document-management/routing/routingForm';
import type { PersonNameParts } from '../../../document-management/routing/routingTypes';
import { personnelApi, personsApi } from '../../../human-resources/personnel/personnelApi';
import { pressConferencesApi } from '../pressConferenceApi';
import {
  PRESS_CONFERENCE_REMARKS_MAX,
  PRESS_CONFERENCE_REQUESTOR_TYPE,
  PRESS_CONFERENCE_PURPOSE_MAX,
  PRESS_CONFERENCE_VENUE_MAX,
  type PressConferenceItem,
  type PressConferenceRequestorType,
} from '../pressConferenceTypes';
import { samplePressConference } from '../utils/samplePressConference';

/** The three parts of the form. A profile card edits ONE; a create, and the row's Edit, step through all. */
export type PressConferenceSection = 'details' | 'requestor' | 'speakers';

/* THREE STEPS — product call, 13 Oct 2026: Requestor · Speaker · Purpose (Purpose to Remarks). */
const STEPS: PressConferenceSection[] = ['requestor', 'speakers', 'details'];

const STEP_LABEL: Record<PressConferenceSection, string> = {
  requestor: 'Requestor',
  speakers: 'Speaker',
  details: 'Purpose',
};

/** Which step owns each field the server can name — anything unlisted is the requestor's. */
const FIELD_STEP: Record<string, PressConferenceSection> = {
  purpose: 'details',
  scheduledDate: 'details',
  startTime: 'details',
  endTime: 'details',
  venue: 'details',
  remarks: 'details',
  speakerPersonnelIds: 'speakers',
};

type RequestorKind = '' | 'external' | 'internal';

const REQUESTOR_OPTIONS: RadioOption<'external' | 'internal'>[] = [
  {
    value: 'external',
    label: 'External organization',
    description: 'An organization outside, typed in — with the person there who asked.',
  },
  { value: 'internal', label: 'Internal employee', description: 'An employee of this organization, picked from Personnel.' },
];

/** The organization cap — `PressConferenceFieldRules.OrganizationMaxLength`. */
const ORGANIZATION_MAX = 200;

interface Employee {
  id: string;
  name: string;
  employeeNumber: string;
}

/**
 * Schedule or correct one press conference — its schedule, who requested it, and who speaks.
 *
 * **No status on the form** — marking one held or cancelled is its own action under its own key.
 *
 * **The REQUESTOR is one of two**, the received document's sender shape: an external organization
 * with the person there who asked — whose name is written to the identity store and never read back
 * here, so on an edit BLANK KEEPS the person already recorded — or an internal employee picked from
 * Personnel. ONE side reaches the wire; the other is sent empty, never stale.
 *
 * **The SPEAKERS are `<SearchTableInput>`** — the standard search-then-table, cut to SPEAKER · ACTION
 * (product call, 13 Oct 2026). Picking ADDS a row; Remove takes it off. Employees only, by id; each
 * name is resolved in ONE batch for the page, never stored.
 *
 * **`section`** narrows the form to one profile card; the rest is sent back as stored. A record with
 * no requestor yet (scheduled before the requestor existed) shows that section with any edit, or the
 * save would be refused for a field not on screen.
 */
export function PressConferenceModal({
  editing,
  section,
  onClose,
  onSaved,
}: {
  /** Null schedules a new one. */
  editing: PressConferenceItem | null;
  section?: PressConferenceSection;
  onClose: () => void;
  onSaved: (saved: PressConferenceItem) => void;
}) {
  const toast = useToast();

  // Implied by `press-conferences.create`/`update`, scoped to the forms — the pickers need to FIND an employee.
  const canReadPeople = usePermission('personnel.read');

  const missingRequestor = editing !== null && editing.requestorType == null;

  /*
    STEPPED for the whole record (a create, and the list row's Edit) — Requestor · Speaker · Purpose
    (product call, 13 Oct 2026); ONE SECTION, unstepped, for a profile card's Edit. A record with no
    requestor yet shows that section with any card's edit, or the save would be refused for a field
    not on screen.
  */
  const stepped = section === undefined;
  const [stepIndex, setStepIndex] = useState(0);
  const currentStep = STEPS[Math.min(stepIndex, STEPS.length - 1)];
  const isLastStep = !stepped || stepIndex === STEPS.length - 1;

  const shows = (part: PressConferenceSection) =>
    stepped ? part === currentStep : section === part || (part === 'requestor' && missingRequestor);

  /** Autogenerate fills EVERY step of a stepped form, and only the open section of a card's edit. */
  const fills = (part: PressConferenceSection) => stepped || shows(part);

  // Seeded once, on mount — the parent mounts this only while it is open.
  const [purpose, setPurpose] = useState(editing?.purpose ?? '');
  const [scheduledDate, setScheduledDate] = useState(editing?.scheduledDate ?? '');
  const [startTime, setStartTime] = useState(editing ? editing.startTime.slice(0, 5) : '');
  const [endTime, setEndTime] = useState(editing?.endTime != null ? editing.endTime.slice(0, 5) : '');
  const [venue, setVenue] = useState(editing?.venue ?? '');
  const [remarks, setRemarks] = useState(editing?.remarks ?? '');

  // A create seeds the choice EMPTY — neither answer is a default anybody gave.
  const [requestorKind, setRequestorKind] = useState<RequestorKind>(
    editing?.requestorType == null
      ? ''
      : editing.requestorType === PRESS_CONFERENCE_REQUESTOR_TYPE.external
        ? 'external'
        : 'internal',
  );
  const [organization, setOrganization] = useState(editing?.requestorOrganization ?? '');
  const [requestorName, setRequestorName] = useState<PersonNameParts>(EMPTY_NAME);
  const [requestorPersonnelId, setRequestorPersonnelId] = useState(editing?.requestorPersonnelId ?? '');
  const [speakerIds, setSpeakerIds] = useState<string[]>(editing?.speakers?.map((s) => s.personnelId) ?? []);

  const [staff, setStaff] = useState<Employee[]>([]);

  // Names for the employees the RECORD already names — the speakers and the internal requestor —
  // even where the live feed no longer offers them (one who has left).
  const [recorded, setRecorded] = useState<Employee[]>([]);
  // Nothing loads without the grant, so the pickers start settled rather than spinning.
  const [loadingStaff, setLoadingStaff] = useState(canReadPeople);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!canReadPeople) return;

    const controller = new AbortController();

    // Fails SOFT — a picker that could not load leaves its list empty and the rest of the form usable.
    personnelApi
      .list({ pageSize: 100 }, controller.signal)
      .then(async (personnel) => {
        const recordedRows = [
          ...(editing?.speakers ?? []).map((s) => ({ id: s.personnelId, personId: s.personId, number: s.employeeNumber })),
          ...(editing?.requestorPersonnelId != null
            ? [{ id: editing.requestorPersonnelId, personId: editing.requestorPersonnelPersonId, number: editing.requestorEmployeeNumber }]
            : []),
        ];

        /*
          ONE batch resolve for every id on screen. A file above this reader's level arrives
          Restricted — that is the mask — and the option still says which employee by number.
        */
        // TWO calls, never one sliced to fit — the resolve caps a batch at 100 and the feed alone can fill it.
        const resolve = (ids: (string | null | undefined)[]) => {
          const unique = [...new Set(ids.filter((id): id is string => id != null))];
          return unique.length > 0 ? personsApi.resolve(unique, controller.signal) : Promise.resolve([]);
        };

        const [feedNames, recordedNames] = await Promise.all([
          resolve(personnel.items.map((p) => p.personId)),
          resolve(recordedRows.map((r) => r.personId)),
        ]);
        const names = [...feedNames, ...recordedNames];

        if (controller.signal.aborted) return;

        const byPersonId = new Map(names.map((n) => [n.personId, n.displayName]));

        setStaff(
          personnel.items.map((p) => ({
            id: p.id,
            name: (p.personId != null ? byPersonId.get(p.personId) : undefined) ?? p.employeeNumber,
            employeeNumber: p.employeeNumber,
          })),
        );
        setRecorded(
          recordedRows.map((r) => ({
            id: r.id,
            name: (r.personId != null ? byPersonId.get(r.personId) : undefined) ?? r.number ?? 'Employee',
            employeeNumber: r.number ?? '',
          })),
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!controller.signal.aborted) setLoadingStaff(false);
      });

    return () => controller.abort();
  }, [canReadPeople, editing]);

  const employees = useMemo(() => {
    const map = new Map<string, Employee>();
    for (const e of recorded) map.set(e.id, e);
    for (const e of staff) map.set(e.id, e);
    return map;
  }, [staff, recorded]);

  const external = requestorKind === 'external';
  const internal = requestorKind === 'internal';

  // An external person ALREADY recorded may be left blank on an edit — blank keeps it.
  const alreadyNamed =
    editing?.requestorType === PRESS_CONFERENCE_REQUESTOR_TYPE.external && editing.requestorPersonId != null;
  const nameRequired = external && !alreadyNamed;

  // `HH:mm` strings compare correctly as strings.
  const endBeforeStart = startTime !== '' && endTime !== '' && endTime <= startTime;

  const requestorValid =
    requestorKind !== '' &&
    (external
      ? organization.trim() !== '' && !nameIncomplete(requestorName) && (!nameRequired || anyNamePart(requestorName))
      : requestorPersonnelId !== '');

  /** What each step still owes, in words — Next is disabled with it, and Submit with the first. */
  const missingOn: Record<PressConferenceSection, string | null> = {
    requestor:
      requestorKind === ''
        ? 'Say who requested it.'
        : requestorValid
          ? null
          : external
            ? organization.trim() === ''
              ? 'Name the organization that requested it.'
              : 'Give the first and last name of the person who requested it.'
            : 'Choose the employee who requested it.',
    // Optional — a conference may be scheduled before anybody is booked to speak.
    speakers: null,
    details:
      purpose.trim() === ''
        ? 'State the purpose.'
        : scheduledDate === ''
          ? 'Choose the date.'
          : startTime === ''
            ? 'Choose the start time.'
            : venue.trim() === ''
              ? 'Say where it is held.'
              : endBeforeStart
                ? 'The end time must come after the start time.'
                : null,
  };

  const missing = STEPS.map((step) => missingOn[step]).find((m) => m !== null) ?? null;
  const valid = missing === null;
  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    const named = external && anyNamePart(requestorName);

    const request = {
      purpose: purpose.trim(),
      scheduledDate: scheduledDate || null,
      startTime: startTime ? `${startTime}:00` : null,
      endTime: endTime ? `${endTime}:00` : null,
      venue: venue.trim(),
      remarks: remarks.trim() || null,
      requestorType: (external
        ? PRESS_CONFERENCE_REQUESTOR_TYPE.external
        : PRESS_CONFERENCE_REQUESTOR_TYPE.internal) as PressConferenceRequestorType,

      // ONE side reaches the wire; the other is cleared, never sent stale.
      requestorOrganization: external ? organization.trim() : null,
      requestorSalutation: named ? requestorName.salutation.trim() || null : null,
      requestorFirstName: named ? requestorName.firstName.trim() || null : null,
      requestorMiddleName: named ? requestorName.middleName.trim() || null : null,
      requestorLastName: named ? requestorName.lastName.trim() || null : null,
      requestorSuffix: named ? requestorName.suffix.trim() || null : null,
      requestorPersonnelId: internal ? requestorPersonnelId : null,
      speakerPersonnelIds: speakerIds,
    };

    try {
      const saved = editing ? await pressConferencesApi.update(editing.id, request) : await pressConferencesApi.create(request);

      toast.success(editing ? `Saved ${saved.referenceNumber}.` : `Scheduled ${saved.referenceNumber}.`);
      onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the press conference.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: 'press-conference',
        // The radio puts its id on each OPTION; the speaker list is one picker standing for the set.
        fieldIds: {
          requestorType: 'press-conference-requestor-type-external',
          RequestorType: 'press-conference-requestor-type-external',
          speakerPersonnelIds: 'press-conference-speaker-add',
          SpeakerPersonnelIds: 'press-conference-speaker-add',
        },
        // A stepped form goes to the step owning the field first. Keys arrive in either case.
        ...(stepped
          ? {
              stepOf: (field: string) =>
                STEPS.indexOf(FIELD_STEP[field.charAt(0).toLowerCase() + field.slice(1)] ?? 'requestor'),
              onStep: setStepIndex,
            }
          : {}),
      });
    } finally {
      setSaving(false);
    }
  }

  const modalTitle = editing
    ? section === 'requestor'
      ? `Edit requestor — ${editing.referenceNumber}`
      : section === 'speakers'
        ? `Edit speakers — ${editing.referenceNumber}`
        : `Edit ${editing.referenceNumber}`
    : 'New press conference';

  return (
    <Modal
      open
      onClose={onClose}
      title={modalTitle}
      // The stepped form (create, and the row's Edit) is 2xl — the received document's three-step form; a card's one section stays xl.
      size={stepped ? '2xl' : 'xl'}
      id="press-conference-modal"
      autogenerate={{
        onGenerate: () => {
          const sample = samplePressConference(staff.map((s) => s.id));

          // Only the sections on screen — a field the reader cannot see is never rewritten behind them.
          if (fills('details')) {
            setPurpose(sample.purpose);
            setScheduledDate(sample.scheduledDate);
            setStartTime(sample.startTime);
            setEndTime(sample.endTime);
            setVenue(sample.venue);
            setRemarks(sample.remarks);
          }

          if (fills('requestor')) {
            setRequestorKind('external');
            setOrganization(sample.organization);
            setRequestorName(sample.requestorName);
            setRequestorPersonnelId(sample.requestorPersonnelId ?? '');
          }

          if (fills('speakers')) setSpeakerIds(sample.speakerPersonnelIds);
        },
        disabled: saving || loadingStaff,
      }}
      // The tick appears on the LAST step — the strip arriving IS the signal nothing is left to fill.
      confirm={
        isLastStep
          ? { id: 'press-conference-confirm', checked: confirmed, onChange: setConfirmed, disabled: saving }
          : undefined
      }
      footer={
        <>
          <Button id="press-conference-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>

          {stepped && stepIndex > 0 ? (
            // Back never validates — it is how a reader FIXES an earlier step.
            <Button
              id="press-conference-back"
              variant="secondary"
              onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              disabled={saving}
            >
              Back
            </Button>
          ) : null}

          {stepped && !isLastStep ? (
            <Tooltip disabledReason={missingOn[currentStep]}>
              <Button
                id="press-conference-next"
                variant="secondary"
                onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))}
                disabled={missingOn[currentStep] !== null}
              >
                Next
              </Button>
            </Tooltip>
          ) : null}

          {/* The primary stays visible on every step, disabled until every step is valid and ticked. */}
          <Tooltip disabledReason={missing}>
            <Button
              id="press-conference-submit"
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {stepped ? (
          <Stepper
            id="press-conference"
            steps={STEPS.map((key) => ({ key, label: STEP_LABEL[key] }))}
            activeIndex={stepIndex}
            onJump={(index) => {
              // Backwards always; forwards only past steps that are already answered.
              if (index <= stepIndex || STEPS.slice(0, index).every((step) => missingOn[step] === null)) setStepIndex(index);
            }}
          />
        ) : null}

        {shows('details') ? (
          <>
            {/* THE PURPOSE takes the full row — it is what the calendar and the register call it. */}
            <TextField
              id="press-conference-purpose"
              label="Purpose"
              required
              description="Why the press conference is being held — the line the calendar and the list show."
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              maxLength={PRESS_CONFERENCE_PURPOSE_MAX}
              disabled={saving}
              error={error?.fieldError('purpose')}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <DateField
                id="press-conference-scheduled-date"
                label="Date"
                required
                description="The day it is held."
                value={scheduledDate}
                onChange={setScheduledDate}
                disabled={saving}
                error={error?.fieldError('scheduledDate')}
              />

              <TextField
                id="press-conference-venue"
                label="Venue"
                required
                description="Where it is held."
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                maxLength={PRESS_CONFERENCE_VENUE_MAX}
                disabled={saving}
                error={error?.fieldError('venue')}
              />

              <TextField
                id="press-conference-start-time"
                type="time"
                label="Start time"
                required
                description="When it starts, in the organization's own time."
                className="font-mono"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                disabled={saving}
                error={error?.fieldError('startTime')}
              />

              <TextField
                id="press-conference-end-time"
                type="time"
                label="End time"
                description="When it is expected to end. Leave it empty if nobody has set one."
                className="font-mono"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={saving}
                error={endBeforeStart ? 'The end time must come after the start time.' : error?.fieldError('endTime')}
              />
            </div>

            {/* FULL WIDTH and outside the grid — a note about the conference as a whole. */}
            <Textarea
              id="press-conference-remarks"
              label="Remarks"
              description="Anything worth knowing about the conference as a whole — what to prepare, who to expect."
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              maxLength={PRESS_CONFERENCE_REMARKS_MAX}
              disabled={saving}
              error={error?.fieldError('remarks')}
            />
          </>
        ) : null}

        {shows('requestor') ? (
          <FormFieldset legend="Requestor">
            <div className="flex flex-col gap-4">
              <RadioGroup<'external' | 'internal'>
                id="press-conference-requestor-type"
                label="Requested by"
                required
                value={requestorKind as 'external' | 'internal'}
                onChange={setRequestorKind}
                options={REQUESTOR_OPTIONS}
                columns={2}
                disabled={saving}
                error={error?.fieldError('requestorType')}
              />

              {external ? (
                <>
                  <TextField
                    id="press-conference-requestor-organization"
                    label="Organization"
                    required
                    description="The organization outside that requested it."
                    value={organization}
                    onChange={(e) => setOrganization(e.target.value)}
                    maxLength={ORGANIZATION_MAX}
                    disabled={saving}
                    error={error?.fieldError('requestorOrganization')}
                  />

                  <CorrespondentNameFields
                    idPrefix="press-conference"
                    role="requestor"
                    field="requestor"
                    value={requestorName}
                    onChange={setRequestorName}
                    editing={alreadyNamed}
                    required={nameRequired}
                    intro={
                      alreadyNamed
                        ? 'The name lives in the identity store, so this form cannot show the one already recorded — the profile does. Leaving these blank KEEPS that person; filling them replaces the name.'
                        : 'The PERSON at the organization who requested it. The name is written to the identity store and never kept on the press conference.'
                    }
                    error={error}
                    disabled={saving}
                  />
                </>
              ) : internal ? (
                canReadPeople ? (
                  <Select
                    id="press-conference-requestor-personnel-id"
                    label="Employee"
                    required
                    description="The employee who requested it. The name is read from the personnel file, never stored here."
                    placeholder="Choose an employee"
                    value={requestorPersonnelId}
                    onChange={setRequestorPersonnelId}
                    options={[...employees.values()].map((e) => ({
                      value: e.id,
                      label: e.name,
                      sublabel: e.employeeNumber,
                      keywords: e.employeeNumber,
                    }))}
                    remote
                    loading={loadingStaff}
                    disabled={saving}
                    error={error?.fieldError('requestorPersonnelId')}
                  />
                ) : (
                  <p className="text-xs text-slate-500">
                    Picking an employee needs access to Personnel, which this account does not have.
                  </p>
                )
              ) : null}
            </div>
          </FormFieldset>
        ) : null}

        {shows('speakers') ? (
          <FormFieldset legend="Speakers">
            {/* THE standard several-records input — search, then a table, Remove as an icon. */}
            <SearchTableInput
              id="press-conference-speaker-add"
              columnLabel="Speaker"
              searchPlaceholder="Search employees to add as speakers…"
              options={[...employees.values()].map((e) => ({
                value: e.id,
                label: e.name,
                sublabel: e.employeeNumber,
                keywords: e.employeeNumber,
              }))}
              rows={speakerIds.map((id) => {
                const employee = employees.get(id);

                return {
                  key: id,
                  primary: employee?.name ?? (loadingStaff ? 'Loading…' : 'Employee'),
                  secondary: employee?.employeeNumber ?? null,
                  secondaryMono: true,
                };
              })}
              onAdd={(id) => setSpeakerIds((current) => (current.includes(id) ? current : [...current, id]))}
              onRemove={(id) => setSpeakerIds((current) => current.filter((x) => x !== id))}
              emptyMessage="No speakers yet. Search above to add one."
              unavailableMessage={
                canReadPeople ? null : 'Picking speakers needs access to Personnel, which this account does not have.'
              }
              remote
              loading={loadingStaff}
              disabled={saving}
              error={error?.fieldError('speakerPersonnelIds')}
            />
          </FormFieldset>
        ) : null}
      </div>
    </Modal>
  );
}
